import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ticketService } from '../../../services/ticketService';
import { useAuth } from '../../../contexts/AuthContext';

const TicketDetailModal = ({ ticket, onClose, onUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchMessages();
  }, [ticket?.id]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await ticketService?.getMessages(ticket?.id);
      setMessages(data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage?.trim()) return;

    try {
      setSending(true);
      await ticketService?.addMessage(ticket?.id, newMessage);
      setNewMessage('');
      await fetchMessages();
      await onUpdate();
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Mesaj gönderilirken bir hata oluştu: ' + err?.message);
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      open: 'Açık',
      in_progress: 'İşlemde',
      resolved: 'Çözüldü',
      closed: 'Kapatıldı'
    };
    return texts?.[status] || status;
  };

  const getCategoryText = (category) => {
    const texts = {
      technical_issues: 'Teknik Sorun',
      billing: 'Faturalama',
      account: 'Hesap',
      feature_request: 'Özellik İsteği',
      bug_report: 'Hata Bildirimi',
      general_inquiry: 'Genel Soru'
    };
    return texts?.[category] || category;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-border p-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm font-mono text-muted-foreground">#{ticket?.ticketNumber}</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket?.status)}`}>
                {getStatusText(ticket?.status)}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">{ticket?.subject}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Tag" size={16} />
                <span>{getCategoryText(ticket?.category)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={16} />
                <span>{new Date(ticket?.createdAt)?.toLocaleDateString('tr-TR')}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors flex-shrink-0"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Initial Description */}
          <div className="bg-muted/50 rounded-lg p-4 border border-border">
            <div className="flex items-start gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={16} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">Siz</p>
                <p className="text-xs text-muted-foreground">{new Date(ticket?.createdAt)?.toLocaleString('tr-TR')}</p>
              </div>
            </div>
            <p className="text-foreground whitespace-pre-wrap">{ticket?.description}</p>
          </div>

          {/* Message Thread */}
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Mesajlar yükleniyor...</p>
            </div>
          ) : messages?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="MessageCircle" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Henüz yanıt yok</p>
            </div>
          ) : (
            messages?.map((message) => {
              const isCurrentUser = message?.senderId === user?.id;
              const isAgent = message?.senderRole === 'admin' || message?.senderRole === 'agent';
              return (
                <div
                  key={message?.id}
                  className={`rounded-lg p-4 border ${
                    !isCurrentUser && isAgent ? 'bg-blue-50 border-blue-200' : 'bg-muted/50 border-border'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        !isCurrentUser && isAgent ? 'bg-blue-600 text-white' : 'bg-primary text-primary-foreground'
                      }`}
                    >
                      <Icon name={!isCurrentUser && isAgent ? 'Headphones' : 'User'} size={16} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">
                        {isCurrentUser ? 'Siz' : (isAgent ? 'Destek Ekibi' : message?.senderName || 'Kullanıcı')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(message?.createdAt)?.toLocaleString('tr-TR')}
                      </p>
                    </div>
                  </div>
                  <p className="text-foreground whitespace-pre-wrap">{message?.message}</p>
                </div>
              );
            })
          )}
        </div>

        {/* Reply Form */}
        {ticket?.status !== 'closed' && (
          <form onSubmit={handleSendMessage} className="border-t border-border p-6">
            <div className="flex gap-3">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                placeholder="Mesajınızı yazın..."
                rows={3}
                className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                disabled={sending}
              />
              <Button
                type="submit"
                variant="default"
                size="default"
                iconName="Send"
                disabled={sending || !newMessage?.trim()}
              >
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TicketDetailModal;